import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toolbar } from "primereact/toolbar";
import React, { useEffect, useRef, useState } from "react";
import { Search } from "../../shared/Search";
import { EmptyList, TFormState, TListState } from "../../shared/modal";
import { EmptyReqFilter, TReqFilter, TRequirement } from "./modal";
import { Fetch, ILazyParams } from "../../utilities/fetch";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Filter } from "./Filter";
import SubmissionForm from "./SubmissionForm";
import { JobTypeLabel, ReqPriorityLabel, ReqTermLabel, ReqTypeLabel } from "../requirement-my-sales/modal";
import { format } from "date-fns";

type TL = TListState<TRequirement>;
type SF = TFormState<TRequirement | undefined>;

export const Table = () => {
    const [table, setTable] = useState(EmptyList as TL);
    const [globalSearch, setGlobalSearch] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [filters, setFilters] = useState<TReqFilter>(EmptyReqFilter);

    const [submission, setSubmission] = useState({ open: false, data: undefined } as SF);
    const hideSubmission = () => setSubmission({ open: false, data: undefined });

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const loadTableData = async () => {
        setTable(prev => ({ ...prev, loading: true }));
        const result = await Fetch<TL>("RecruiterMyRequirement",
            {
                query: new URLSearchParams({
                    lazyParams: JSON.stringify(lazyParams),
                    filters: JSON.stringify({
                        ...filters,
                        fromDate: filters.fromDate ? format(filters.fromDate as Date, 'yyyy-MM-dd') : null,
                        toDate: filters.toDate ? format(filters.toDate as Date, 'yyyy-MM-dd') : null
                    }),
                    ...(globalSearch ? { search: globalSearch } : {})
                }).toString()
            });
        if (result) {
            setTable({ data: result.data, count: result.count, loading: false });
        }
        setTable(prev => ({ ...prev, loading: false }));
    }


    useEffect(() => {
        loadTableData();
    }, [globalSearch, lazyParams, filters]);

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">Manage Requirements</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <Search setSearch={setGlobalSearch} />
                <Button icon="pi pi-upload" className="p-button-help ml-2" onClick={exportCSV} />
            </span>
        </div>
    );

    return (
        <>
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter filters={filters} setFilters={(filters: TReqFilter) => setFilters(filters)} loading={table.loading} />
                </AccordionTab>
            </Accordion>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small"
                value={table.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={table.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No Requirement found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={table.loading}
            >
                <Column field="id" header="Req ID"
                    body={(r: TRequirement) => (
                        <span onClick={() => setSubmission({ open: true, data: r })}
                            title="Submit Candidate"
                            className="clickable-id">
                            #{r.id}
                        </span>
                    )}
                />
                <Column field="jobTitle" header="Job Title" />
                <Column field="postedBy" header="Sales Manager" />
                {/* <Column field="reqType" header="Req Type" body={(r: TRequirement) => ReqTypeLabel[r.reqType]} /> */}
                <Column field="duration" header="Duration" body={(r: TRequirement) => `${r.duration} month(s)`} />
                <Column field="location" header="Location" body={(r: TRequirement) => r.cityId ? `${r.cityName}, ${r.stateName}, ${r.countryName}` : '--'} />
                <Column field="rate" header="Rate" />
                <Column field="term" header="Term" body={(r: TRequirement) => ReqTermLabel[r.term]} />
                <Column field="skillCategory" header="Skill Category" />
                <Column field="latency" header="Latency" body={(r: TRequirement) => {
                    const postedOn = new Date(r.postedOn + 'z');
                    const now = new Date();
                    const diffInMs = now.getTime() - postedOn.getTime();
                    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
                    return `${diffInDays} day(s)`;
                }} />
                <Column field="requiredSkill" header="Required Skills" />
                <Column field="isHold" header="Status" body={(r: TRequirement) => r.isHold ? 'Hold' : 'Unhold'} />
                {/* <Column field="jobType" header="Job Type" body={(r: TRequirement) => JobTypeLabel[r.jobType]} />
                <Column field="priority" header="Priority" body={(r: TRequirement) => ReqPriorityLabel[r.priority]} />
                <Column field="industryName" header="Industry" />
                <Column field="interviewProcess" header="Interview Process" />
                <Column field="vendorCompanyName" header="Client Company" />
                <Column field="contactPersonName" header="Contact Person" />
                <Column field="visa" header="Visa" body={(r: TRequirement) => <span>{r.visa?.map(v => v.name).join(', ')}</span>} />
                <Column field="postedOn" header="Posted On" /> */}
            </DataTable>
            <SubmissionForm open={submission.open} req={submission.data} hideDialog={hideSubmission} />
        </>
    );
}
